import { Component, Input, ViewEncapsulation, OnInit, OnDestroy } from '@angular/core';
import { Subscription, interval } from 'rxjs';
import { Platform } from '@ionic/angular';
import { Router, NavigationEnd } from '@angular/router';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { NotificationsProvider } from 'src/providers/notifications/notifications';
import { NotificationUpdateService } from 'src/shared/providers/notification-update/notification-update.service';

import { bottomMenuItems } from 'src/shared/components/bottom-nav/bottom-nav-items';

@Component({
    selector: 'tidy-bottom-nav',
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./bottom-nav.component.scss'],
    template: `
    <nav>
      <ul class="menu">
        <li
          class="menu-item"
          [class.active-item]="isActiveItem(item)"
          [attr.data-cy]="item.label"
          *ngFor="let item of menuItems"
          (click)="navigateTo(item.target)">
          <div class="icon-container">
            <tidy-image
              [isActive]="isActiveItem(item)"
              [src]="item.icon"
              class="title-size no-right-margin">
            </tidy-image>
            <div
              *ngIf="shouldShowBadge(item)"
              class="notification-badge"
              [attr.data-count]="getFormattedNotificationCount()">
              {{getFormattedNotificationCount()}}
            </div>
          </div>
          <span class="item-menu" [class.active]="isActiveItem(item)">
            {{item.label}}
          </span>
        </li>
      </ul>
    </nav>
  `,
    standalone: false
})
export class BottomNavComponent implements OnInit, OnDestroy {

  @Input() activeRoute: string;
  menuItems = bottomMenuItems;
  notificationCount: number = 0;
  private notificationSubscription: Subscription;
  private pingSubscription: Subscription;
  private platformResumeSubscription: Subscription;
  private routerSubscription: Subscription;
  private readonly PING_INTERVAL = 60000; // 1 minute in milliseconds
  private isAppActive: boolean = true;
  private lastFetchTime: number = 0;
  private readonly FETCH_THROTTLE = 5000; // 5 seconds throttle
  private isOnDashboard: boolean = false;

  constructor(
    private customNavController: CustomNavController,
    private notificationsProvider: NotificationsProvider,
    private platform: Platform,
    private notificationUpdateService: NotificationUpdateService,
    private router: Router
  ) {}

  ngOnInit() {
    // Subscribe to notification count changes
    this.notificationSubscription = this.notificationsProvider.getNotificationCount()
      .subscribe(count => {
        this.notificationCount = count;
      });

    // Start periodic ping to update notification count
    this.startNotificationPing();

    // Listen for app resume to refresh notification count
    this.platformResumeSubscription = this.platform.resume.subscribe(() => {
      this.isAppActive = true;
      this.fetchNotificationCount();
    });

    // Listen for app pause to stop pinging when app is in background
    this.platform.pause.subscribe(() => {
      this.isAppActive = false;
    });

    // Check if currently on dashboard
    this.updateDashboardStatus();

    // Listen for route changes to update dashboard status
    this.routerSubscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.isOnDashboard = event.url === '/dashboard';
        console.log(`Navigation to: ${event.url}, isOnDashboard: ${this.isOnDashboard}`);
      }
    });
  }

  ngOnDestroy() {
    if (this.notificationSubscription) {
      this.notificationSubscription.unsubscribe();
    }
    if (this.pingSubscription) {
      this.pingSubscription.unsubscribe();
    }
    if (this.platformResumeSubscription) {
      this.platformResumeSubscription.unsubscribe();
    }
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

    private startNotificationPing() {
    // Initial fetch
    this.fetchNotificationCount();

    // Set up periodic ping every minute
    this.pingSubscription = interval(this.PING_INTERVAL)
      .subscribe(() => {
        // Only ping if app is active
        if (this.isAppActive) {
          this.fetchNotificationCount();
        }
      });
  }

  private updateDashboardStatus() {
    this.isOnDashboard = this.activeRoute === '/dashboard';
  }

    public async fetchNotificationCount() {
    const now = Date.now();

    // Throttle API calls to prevent too many requests
    if (now - this.lastFetchTime < this.FETCH_THROTTLE) {
      console.log('Notification fetch throttled');
      return;
    }

    this.lastFetchTime = now;

    try {
      console.log(`Fetching notifications (on dashboard: ${this.isOnDashboard})...`);

      if (this.isOnDashboard) {
        // If on dashboard, fetch full notification list to update the UI
        await this.fetchFullNotificationList();
      } else {
        // If not on dashboard, only fetch count for the badge
        await this.fetchNotificationCountOnly();
      }
    } catch (error) {
      console.error('Error fetching notification count:', error);
    }
  }

  private async fetchFullNotificationList() {
    try {
      const notifications = await this.notificationsProvider.getNotifications(1, 11);
      const unreadCount = notifications.body.filter((notification: any) => !notification.is_read).length;
      this.notificationsProvider.setNotificationCount(unreadCount);
      console.log(`Updated full notification list and count: ${unreadCount}`);

      // Emit an event to update the dashboard notification list
      this.emitNotificationUpdate(notifications);
    } catch (error) {
      console.error('Error fetching full notification list:', error);
    }
  }

  private async fetchNotificationCountOnly() {
    try {
      const notifications = await this.notificationsProvider.getNotifications(1, 11);
      const unreadCount = notifications.body.filter((notification: any) => !notification.is_read).length;
      this.notificationsProvider.setNotificationCount(unreadCount);
      console.log(`Updated notification count only: ${unreadCount}`);
    } catch (error) {
      console.error('Error fetching notification count only:', error);
    }
  }

  private emitNotificationUpdate(notifications: any) {
    // Emit notification update for dashboard component
    this.notificationUpdateService.emitNotificationUpdate(notifications);
    console.log('Emitting notification update for dashboard');
  }

  navigateTo(url) {
    // Update dashboard status
    this.isOnDashboard = url === '/dashboard';

    // If navigating to dashboard, refresh notification count immediately
    if (this.isOnDashboard) {
      this.fetchNotificationCount();
    }

    this.customNavController.navigateForward(url);
  }

  isActiveItem(item) {
    return this.activeRoute === item.target;
  }

  getNotificationCount() {
    return this.notificationCount;
  }

  getFormattedNotificationCount() {
    if (this.notificationCount > 99) {
      return '99+';
    }
    return this.notificationCount.toString();
  }

  shouldShowBadge(item) {
    return item.label === 'Dashboard' && this.notificationCount > 0;
  }

  // Public method to manually refresh notification count
  public refreshNotificationCount() {
    this.fetchNotificationCount();
  }

}
